import {
  forwardRef,
  type HTMLAttributes,
  type MouseEvent,
  type ReactNode,
  type Ref,
} from "react";
import { Check } from "lucide-react";
import { cn } from "../../utils/classes";
import styles from "./list-item.module.css";

export type ListItemProps = Omit<HTMLAttributes<HTMLElement>, "onClick"> & {
  /** SM 32px for dense menus, MD 40px for dropdowns, LG 48px for lists and mobile sheets. */
  size?: "sm" | "md" | "lg";
  /** Avatar, icon or logo shown before the label. */
  leading?: ReactNode;
  description?: ReactNode;
  /** Meta text or control after the label; replaced by the check when selected. */
  trailing?: ReactNode;
  selected?: boolean;
  disabled?: boolean;
  divider?: boolean;
  /** When set, the row renders as a button. Otherwise it is a plain row. */
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  children: ReactNode;
};

/**
 * One row for menus, dropdown options, related records and mobile sheets.
 * Selection semantics (role="option", aria-selected) stay with the consumer's list.
 */
export const ListItem = forwardRef<HTMLElement, ListItemProps>(function ListItem(
  {
    size = "md",
    leading,
    description,
    trailing,
    selected = false,
    disabled = false,
    divider = false,
    onClick,
    className,
    children,
    ...props
  },
  ref,
) {
  const content = (
    <>
      {leading && <span className={styles.leading}>{leading}</span>}
      <span className={styles.text}>
        <span className={styles.label}>{children}</span>
        {description && <span className={styles.description}>{description}</span>}
      </span>
      {selected ? (
        <Check className={styles.check} aria-hidden="true" />
      ) : (
        trailing && <span className={styles.trailing}>{trailing}</span>
      )}
    </>
  );
  const shared = {
    className: cn(styles.item, className),
    "data-size": size,
    "data-selected": selected || undefined,
    "data-divider": divider || undefined,
    "data-disabled": disabled || undefined,
  };
  if (onClick) {
    return (
      <button
        {...(props as HTMLAttributes<HTMLButtonElement>)}
        {...shared}
        ref={ref as Ref<HTMLButtonElement>}
        type="button"
        disabled={disabled}
        onClick={onClick}
      >
        {content}
      </button>
    );
  }
  return (
    <div {...props} {...shared} ref={ref as Ref<HTMLDivElement>} aria-disabled={disabled || undefined}>
      {content}
    </div>
  );
});
